import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckCircleIcon, LockClosedIcon, ClockIcon, AcademicCapIcon } from '@heroicons/react/24/outline';

const statusIcons = {
    completed: CheckCircleIcon,
    in_progress: ClockIcon,
    available: ArrowRightIcon,
    locked: LockClosedIcon,
    catalog: AcademicCapIcon
};

const statusLabels = {
    completed: 'Completed',
    in_progress: 'In Progress',
    available: 'Ready',
    locked: 'Locked',
    catalog: 'Module',
    returned: 'Needs Update',
    submitted: 'Awaiting Review'
};

const statusColors = {
    completed: 'bg-gray-900 text-white border-gray-900',
    in_progress: 'bg-white text-gray-900 border-gray-900',
    available: 'bg-white text-gray-900 border-gray-300',
    locked: 'bg-gray-50 text-gray-500 border-gray-200',
    catalog: 'bg-brand-50 text-brand-800 border-brand-200'
};

const LearningPathCard = ({ item }) => {
    const Icon = statusIcons[item.status] || LockClosedIcon;
    const colorClass = statusColors[item.status] || statusColors.locked;
    const label = statusLabels[item.status] || 'Locked';
    const isLocked = item.status === 'locked';
    const isCatalog = item.status === 'catalog';

    return (
        <div className={`border rounded-lg p-4 ${isLocked ? 'border-gray-200 bg-gray-50' : 'border-gray-200 bg-white'}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <h3 className={`text-sm font-medium truncate ${isLocked ? 'text-gray-400' : 'text-gray-900'}`}>
                        {item.title}
                    </h3>
                    <p className={`text-sm mt-1 line-clamp-2 ${isLocked ? 'text-gray-400' : 'text-gray-600'}`}>
                        {item.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-gray-500">
                        <span>{item.type?.replace(/_/g, ' ') || 'Learning module'}</span>
                        {item.required_hours != null && (
                            <>
                                <span aria-hidden="true">•</span>
                                <span>{item.required_hours} hrs</span>
                            </>
                        )}
                        {item.is_mandatory && (
                            <>
                                <span aria-hidden="true">•</span>
                                <span>Mandatory</span>
                            </>
                        )}
                    </div>
                    {isLocked && (
                        <p className="text-xs text-gray-500 mt-2">Locked — complete earlier modules to unlock.</p>
                    )}
                </div>
                <div className="flex flex-col items-end space-y-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${colorClass}`}>
                        <Icon className="w-3 h-3 mr-1" aria-hidden="true" />
                        {label}
                    </span>
                    {item.progress > 0 && item.progress < 100 && (
                        <div
                            className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden"
                            role="progressbar"
                            aria-valuenow={item.progress}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label={`${item.title} progress`}
                        >
                            <div className="h-full bg-black" style={{ width: `${item.progress}%` }} />
                        </div>
                    )}
                    {(item.status === 'available' || item.status === 'in_progress') && !isCatalog && (
                        item.videoModuleId ? (
                            <Link
                                to={`/learning-modules/${item.videoModuleId}`}
                                className="text-xs font-bold text-white bg-brand-700 hover:bg-brand-800 px-3 py-1.5 rounded-lg transition-colors inline-flex items-center gap-1 shadow-sm mt-1"
                            >
                                ▶ Watch Video & Quiz
                            </Link>
                        ) : (
                            <Link
                                to={`/learning/item/${item.id}`}
                                className="text-xs font-medium text-black hover:underline min-h-[44px] inline-flex items-center"
                            >
                                {item.status === 'available' ? 'Start →' : 'Continue →'}
                            </Link>
                        )
                    )}
                    {isCatalog && (
                        item.videoModuleId ? (
                            <Link
                                to={`/learning-modules/${item.videoModuleId}`}
                                className="text-xs font-bold text-white bg-brand-700 hover:bg-brand-800 px-3 py-1 rounded transition-colors inline-flex items-center gap-1"
                            >
                                ▶ View Video Module
                            </Link>
                        ) : (
                            <Link
                                to={`/learning/item/${item.id}`}
                                className="text-xs font-medium text-brand-700 hover:underline min-h-[44px] inline-flex items-center"
                            >
                                View module →
                            </Link>
                        )
                    )}
                    {item.status === 'completed' && (
                        <div className="flex flex-col items-end gap-1">
                            {item.videoModuleId && (
                                <Link
                                    to={`/learning-modules/${item.videoModuleId}`}
                                    className="text-xs font-semibold text-brand-700 hover:underline flex items-center gap-1"
                                >
                                    ▶ Review Video
                                </Link>
                            )}
                            <Link
                                to="/professional-journey"
                                className="text-xs font-medium text-gray-600 hover:underline"
                            >
                                Document your journey →
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LearningPathCard;
